/**
 * @description AuthCallback component for handling OAuth callback redirects
 *
 * Key features:
 * - Extracts access token from URL fragment (#access_token=...)
 * - Stores token via AuthContext (proper state management)
 * - Fetches user info before navigation (eliminates race condition)
 * - Redirects to dashboard on success
 * - Handles error states from failed auth
 *
 * @dependencies
 * - AuthContext: For handleAuthCallback to store token and fetch user
 * - AuthLayout: Shared auth page layout (for error display)
 *
 * @notes
 * - Backend redirects here after OAuth callback: /auth/callback#access_token=xxx&expires_in=xxx
 * - Token stored in URL fragment (not query params) for security
 * - handleAuthCallback ensures auth state is set BEFORE navigation to dashboard
 */

import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import AuthLayout from './AuthLayout';
import DashboardSkeleton from '../DashboardSkeleton';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * Parse URL hash fragment into key-value pairs
 * @param {string} hash - URL hash string (without #)
 * @returns {object} Parsed key-value pairs
 */
function parseHashParams(hash) {
  if (!hash) return {};

  const params = {};
  const pairs = hash.split('&');

  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key) {
      params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
    }
  }

  return params;
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAuthCallback } = useContext(AuthContext);

  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Check for error in query params (from failed OAuth)
        const searchParams = new URLSearchParams(location.search);
        const queryError = searchParams.get('error');

        if (queryError) {
          const errorDescription = searchParams.get('error_description') || 'Authentication failed';
          setError(getErrorMessage(queryError, errorDescription));
          return;
        }

        // Parse access token from URL fragment
        const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
        const hashParams = parseHashParams(hash);

        const accessToken = hashParams.access_token;
        const expiresIn = hashParams.expires_in;

        if (!accessToken) {
          setError('No access token received. Please try signing in again.');
          return;
        }

        // Clear the URL hash for security (before async operations)
        window.history.replaceState(null, '', location.pathname);

        // Use AuthContext to store token and fetch user info
        // This ensures auth state is set BEFORE navigation, eliminating race condition
        const success = await handleAuthCallback(accessToken, parseInt(expiresIn, 10) || 3600);

        if (!success) {
          setError('Failed to authenticate. Please try signing in again.');
          return;
        }

        // Check for a stored returnUrl (e.g. from AcceptCompanyInvite redirect)
        const returnUrl = sessionStorage.getItem('auth_return_url');
        sessionStorage.removeItem('auth_return_url');

        // Navigate to returnUrl if it exists and is a safe relative path, otherwise dashboard
        if (returnUrl && returnUrl.startsWith('/')) {
          navigate(returnUrl, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }

      } catch (err) {
        console.error('Auth callback error:', err);
        setError('An unexpected error occurred. Please try signing in again.');
      }
    };

    processCallback();
  }, [location, navigate, handleAuthCallback]);

  /**
   * Map OAuth error codes to user-friendly messages
   */
  function getErrorMessage(errorCode, errorDescription) {
    const errorMessages = {
      'access_denied': 'Access was denied. Please try signing in again.',
      'invalid_request': 'Invalid request. Please try signing in again.',
      'invalid_state': 'Session expired. Please try signing in again.',
      'invalid_callback': 'Invalid callback. Please try signing in again.',
      'session_expired': 'Your session has expired. Please sign in again.',
      'callback_failed': 'Authentication failed. Please try again.',
      'login_required': 'Please sign in to continue.',
      'consent_required': 'Additional permissions required. Please try again.',
    };

    return errorMessages[errorCode] || errorDescription || 'Authentication failed. Please try again.';
  }

  // Show error state
  if (error) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication failed
          </h1>
          <p className="text-gray-600 mb-8">
            {error}
          </p>
          <Link
            to="/sign-in"
            className="w-full text-center rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-hover focus:outline-none focus:ring-4 focus:ring-brand"
          >
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Show dashboard skeleton while processing - smoother UX than "Signing you in" message
  return <DashboardSkeleton />;
}

export default AuthCallback;
